// Tableau 20 — Causes de décès (CIM-10). « Saisie rapide » : on choisit cause, âge, sexe, nombre → « Ajouter » (ou Entrée).
import { useMemo, useRef, useState } from 'react';
import { CAUSES_DECES, AGES_DECES, rechercherCause, colonneDeces } from '../../../../shared/business-logic/index.ts';
import type { EvenementDeces, SexeDeces } from '../../../../shared/business-logic/index.ts';
import { useModules } from '../../hooks/useModules.ts';
import { EnteteModule, PasDePeriode, Message, Pastille } from '../../components/ui.tsx';
import type { Periode } from '../Accueil.tsx';

export default function Deces({ periode }: { periode: Periode | null }) {
  const m = useModules(periode);
  const [q, setQ] = useState('');
  const [cause, setCause] = useState<string | null>(null);
  const [age, setAge] = useState(3);
  const [sexe, setSexe] = useState<SexeDeces>('M');
  const [nombre, setNombre] = useState(1);
  const [info, setInfo] = useState<string | null>(null);
  const champRecherche = useRef<HTMLInputElement>(null);
  const suggestions = useMemo(() => rechercherCause(q, 6), [q]);
  const evenements = m.saisies('CAUSES_DECES');

  if (!periode) return <PasDePeriode />;
  if (!m.pret) return <p>Chargement…</p>;
  const calc = m.calc;
  if (!calc) return <Message niveau="warn">{m.erreur ?? 'Données indisponibles.'}</Message>;
  const g = calc.causesDeces.grille;
  const libelle = (code: string) => CAUSES_DECES.find((c) => c.code === code)?.libelle ?? code;

  async function ajouter() {
    if (!cause) { setInfo('Choisissez d\'abord une cause (tapez un code CIM-10 ou un nom).'); return; }
    const evt: EvenementDeces = { cause, age, sexe, nombre: Math.max(1, Math.min(999, nombre)) };
    await m.ecrire('CAUSES_DECES', `d-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`, evt);
    setInfo(`✔ ${cause} ${libelle(cause)} · ${AGES_DECES[age]} · ${sexe === 'F' ? 'Femme' : 'Homme'} · +${evt.nombre}`);
    setNombre(1); setQ(''); setCause(null); champRecherche.current?.focus();
  }
  async function annulerDernier() {
    const dernier = evenements[evenements.length - 1];
    if (!dernier) { setInfo('Rien à annuler.'); return; }
    await m.supprimer('CAUSES_DECES', dernier.cle);
    setInfo('↶ Dernier décès annulé.');
  }

  return (
    <div>
      <EnteteModule titre="Tableau 20 — Causes de décès (CIM-10)" periode={periode} statut={periode.statut} lectureSeule={m.lectureSeule}
        aide={<>Chaque décès se saisit <b>une seule fois</b> (paludisme = B54). Le tableau se remplit tout seul.</>} />
      {!m.lectureSeule && (
        <div className="carte saisie-rapide" onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void ajouter(); } }}>
          <label>Cause (code ou nom)
            <input ref={champRecherche} value={cause ? `${cause} — ${libelle(cause)}` : q} placeholder="ex. B54 ou paludisme" autoComplete="off"
              onChange={(e) => { setCause(null); setQ(e.target.value); }} />
          </label>
          {!cause && q.length >= 1 && suggestions.length > 0 && (
            <ul className="suggestions">{suggestions.map((s) => <li key={s.code}><button type="button" onClick={() => { setCause(s.code); setQ(''); }}><b>{s.code}</b> — {s.libelle}</button></li>)}</ul>
          )}
          <fieldset><legend>Âge</legend>
            <div className="choix">{AGES_DECES.map((a, i) => <label key={a} className={`radio ${age === i ? 'on' : ''}`}><input type="radio" name="age" checked={age === i} onChange={() => setAge(i)} />{a}</label>)}</div>
          </fieldset>
          <div className="ligne-actions">
            <fieldset><legend>Sexe</legend>
              <div className="choix">{(['M', 'F'] as SexeDeces[]).map((s) => <label key={s} className={`radio ${sexe === s ? 'on' : ''}`}><input type="radio" name="sexe" checked={sexe === s} onChange={() => setSexe(s)} />{s === 'M' ? 'Homme' : 'Femme'}</label>)}</div>
            </fieldset>
            <label>Nombre<input className="num" inputMode="numeric" value={nombre} onChange={(e) => setNombre(Number(e.target.value.replace(/\D/g, '').slice(0, 3)) || 1)} onFocus={(e) => e.target.select()} /></label>
            <button type="button" onClick={ajouter}>+ Ajouter au tableau</button>
            <button type="button" className="secondaire" onClick={annulerDernier}>Annuler le dernier</button>
          </div>
          {info && <Message niveau="ok">{info}</Message>}
        </div>
      )}
      <div className="kpis"><div><span>Total décès</span><b>{g.total}</b></div><div><span>Hommes</span><b>{g.totalM}</b></div><div><span>Femmes</span><b>{g.totalF}</b></div></div>
      {calc.causesDeces.anomalies.length > 0 && <Message niveau="warn"><b>Valeurs inhabituelles :</b><ul>{calc.causesDeces.anomalies.map((a: string) => <li key={a}>{a}</li>)}</ul></Message>}
      <div className="defil">
        <table className="saisie compact">
          <thead>
            <tr><th className="fixe" rowSpan={2}>CIM-10 · Cause</th>{AGES_DECES.map((a) => <th key={a} colSpan={2}>{a}</th>)}<th rowSpan={2}>Total</th></tr>
            <tr>{AGES_DECES.flatMap((a) => [<th key={a + 'm'}>M</th>, <th key={a + 'f'}>F</th>])}</tr>
          </thead>
          <tbody>
            {CAUSES_DECES.map((c) => (
              <tr key={c.code}><td className="fixe"><small>{c.code}</small> {c.libelle}</td>
                {g.cellules[c.code].map((v: number, k: number) => <td key={k} className={v ? 'plein' : ''}>{v || ''}</td>)}
                <td className="calcule">{g.totauxLigne[c.code] || ''}</td></tr>
            ))}
          </tbody>
          <tfoot><tr><td className="fixe">Total décès</td>{g.totauxColonne.map((v: number, k: number) => <td key={k}>{v}</td>)}<td>{g.total}</td></tr></tfoot>
        </table>
      </div>
      {evenements.length > 0 && <p className="note"><Pastille>{evenements.length} saisie(s) ce mois</Pastille> Dernières : {evenements.slice(-3).map((e) => { const d = e.donnees as EvenementDeces; return `${d.cause}/${AGES_DECES[d.age]}/${d.sexe}×${d.nombre}`; }).join(' · ')}</p>}
    </div>
  );
}
void colonneDeces;
